import { PurchaseItemStatus } from "common";
import { useMutation, UseMutationResult } from "react-query";
import { CommunicationChannelType, Purchase, PurchaseItem } from "types";
import { api } from "../../../api/api";
import { useSeller, useUser } from "../../../hooks/useAccount";
import { useInvalidate } from "../../../hooks/useInvalidation";
import { isSplitPayPurchase } from "../../../models/Purchase";
import { getCreateMessagePayloadChannels } from "../../Common/MessageModal/MessageModal";
import { FormState } from "../../Common/MessageModal/MessageModalTypes";

export interface AcceptOrDeclinePurchaseValues extends FormState {
    status?: PurchaseItemStatus;
    collectPayment: boolean;
}

type UseAcceptOrDeclinePurchaseMutationHook = (
    purchase: Purchase,
    item: PurchaseItem,
) => UseMutationResult<void, unknown, AcceptOrDeclinePurchaseValues>;

const useAcceptOrDeclinePurchaseMutation: UseAcceptOrDeclinePurchaseMutationHook = (purchase, item) => {
    const invalidate = useInvalidate();
    const seller = useSeller();
    const user = useUser();
    const isSplitPayPayerEnabled = isSplitPayPurchase(purchase);

    return useMutation(
        async ({
            status,
            email,
            sms,
            enableEmail,
            enableSms,
            collectPayment: doNotChargeNowChecked,
        }: AcceptOrDeclinePurchaseValues) => {
            const shouldCollectPayment = !doNotChargeNowChecked && !isSplitPayPayerEnabled;
            await api.patch<Purchase>(`/purchases/${purchase.id}`, {
                items: [
                    {
                        id: item.id,
                        status,
                        message: email.body ?? sms.body ?? "",
                        _collectPayment: shouldCollectPayment,
                    },
                ],
            });

            const allMessageTemplateChannels = getCreateMessagePayloadChannels({ email, sms, enableSms, enableEmail });

            const messageTemplateChannels = allMessageTemplateChannels.filter((channel) => !!channel.body);

            if (purchase.customerEmail && messageTemplateChannels.length > 0) {
                const channels = messageTemplateChannels.map((c) => c.type);
                const formats = messageTemplateChannels.map((c) => ({
                    type: c.type === CommunicationChannelType.SMS ? "text" : c.type,
                    ...(c.subject ? { subject: c.subject } : {}),
                    body: c.body,
                }));
                const messagePayload = {
                    filters: { purchase: { id: purchase.id }, item: item.id },
                    channels,
                    formats,
                    messageTemplate: { channels: messageTemplateChannels },
                    to: [{ type: "traveler", id: purchase.organizer?.id }],
                    from: user.id,
                };

                await api.post(`/sellers/${seller.id}/messages`, messagePayload);
            }

            invalidate("purchases", purchase.id);
        },
    );
};

export default useAcceptOrDeclinePurchaseMutation;
